import { ReactNode } from 'react'
import styled, { css } from 'styled-components'
import { contentWidth } from '../common/common'

// Building blocks shared by the Affiliate tab and the admin affiliate pages, styled like the rest of the
// dashboard (translucent cards on the gradient background, Prompt, 15px radii).

export const Page = styled.div`
    ${contentWidth}
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding-bottom: 60px;

    /* The desktop nav leaves a margin below it; the mobile header doesn't */
    @media only screen and (max-width: 900px) {
        padding-top: 20px;
    }
`

/** Single centred column for forms and reading (pitch, terms, invite), matching Settings and checkout. */
export const Narrow = styled.div`
    width: 100%;
    max-width: 680px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 20px;
`

export const PageTitle = styled.h1`
    font-size: 28px;
    font-weight: 700;
`

export const PageSubtitle = styled.p`
    color: rgba(255, 255, 255, 0.6);
    margin-top: 4px;
`

export const Card = styled.section`
    background: rgba(255, 255, 255, 0.05);
    border-radius: 20px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 14px;
    min-width: 0;
`

export const CardTitle = styled.h2`
    font-size: 18px;
    font-weight: 700;
`

export const Muted = styled.p`
    color: rgba(255, 255, 255, 0.6);
    font-size: 14px;
`

export const Grid = styled.div<{ $min?: number }>`
    display: grid;
    gap: 16px;
    grid-template-columns: repeat(auto-fit, minmax(${({ $min = 200 }) => $min}px, 1fr));
`

export const Row = styled.div<{ $wrap?: boolean; $gap?: number }>`
    display: flex;
    align-items: center;
    gap: ${({ $gap = 10 }) => $gap}px;
    flex-wrap: ${({ $wrap = true }) => ($wrap ? 'wrap' : 'nowrap')};
`

export const Spacer = styled.div`
    flex: 1;
`

/* ---------------------------------- Tiles ---------------------------------- */

const TileBox = styled(Card)`
    gap: 6px;
`

const TileLabel = styled.p`
    color: rgba(255, 255, 255, 0.6);
    font-size: 14px;
    font-weight: 600;
`

const TileValue = styled.p<{ $negative?: boolean }>`
    font-size: 28px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: ${({ $negative }) => ($negative ? '#ff8a94' : 'white')};
`

export const Tile = ({
    label,
    value,
    hint,
    negative,
    children,
}: {
    label: string
    value: ReactNode
    hint?: ReactNode
    negative?: boolean
    children?: ReactNode
}) => (
    <TileBox>
        <TileLabel>{label}</TileLabel>
        <TileValue $negative={negative}>{value}</TileValue>
        {hint && <Muted>{hint}</Muted>}
        {children}
    </TileBox>
)

/* --------------------------------- Buttons --------------------------------- */

// Keyboard focus ring shared by every interactive element in the kit
export const focusRing = css`
    &:focus-visible {
        outline: 2px solid #aab4ff;
        outline-offset: 2px;
    }
`

const buttonBase = css`
    ${focusRing}
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 44px;
    padding: 0 18px;
    border-radius: 30px;
    font-family: 'Prompt', sans-serif;
    font-size: 15px;
    font-weight: 700;
    color: white;
    cursor: pointer;
    white-space: nowrap;
    transition: transform 0.2s ease, opacity 0.2s ease;

    &:hover:not(:disabled) {
        transform: translateY(-2px);
    }

    @media (prefers-reduced-motion: reduce) {
        transition: none;

        &:hover:not(:disabled) {
            transform: none;
        }
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.45;
    }
`

export const PrimaryButton = styled.button`
    ${buttonBase}
    background-image: linear-gradient(180deg, #ff83af 0%, #f45a90 100%);
    box-shadow: 0 5px 10px rgba(0, 0, 0, 0.25);
`

/** The page's one main action, in the purple the dashboard uses for Purchase / Subscribe. */
export const ActionButton = styled.button`
    ${buttonBase}
    background-image: linear-gradient(180deg, #7f89ff 0%, #5762f2 100%);
    box-shadow: 0 5px 10px rgba(0, 0, 0, 0.25);
`

export const SecondaryButton = styled.button`
    ${buttonBase}
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.1);
`

export const DangerButton = styled.button`
    ${buttonBase}
    background-image: linear-gradient(180deg, #eb4e55 0%, #c83e44 100%);
`

export const LinkButton = styled.button`
    ${focusRing}
    background: none;
    padding: 0;
    color: #8fa0ff;
    font-family: inherit;
    font-size: inherit;
    font-weight: 600;
    cursor: pointer;
    text-align: left;
`

/* ---------------------------------- Inputs --------------------------------- */

export const Label = styled.label`
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-size: 14px;
    color: rgba(255, 255, 255, 0.7);
    min-width: 0;
`

const inputBase = css`
    color: white;
    font-family: 'Prompt', sans-serif;
    font-size: 16px;
    font-weight: 500;
    padding: 12px 12px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.07);
    border: 1px solid rgba(255, 255, 255, 0.1);
    outline: none;
    width: 100%;

    &:focus {
        border-color: rgba(255, 255, 255, 0.35);
    }
`

export const Input = styled.input`
    ${inputBase}
`

export const TextArea = styled.textarea`
    ${inputBase}
    min-height: 110px;
    resize: vertical;
`

export const Select = styled.select`
    ${inputBase}
    appearance: none;
    padding-right: 40px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1.5l5 5 5-5' fill='none' stroke='white' stroke-opacity='0.7' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 16px center;
    cursor: pointer;

    option {
        color: black;
    }
`

// Native checkbox restyled to match the inputs: translucent box, pink fill and a white tick when checked
const checkboxBase = css`
    ${focusRing}
    appearance: none;
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    margin: 0;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.07) no-repeat center / 12px 10px;
    border: 1.5px solid rgba(255, 255, 255, 0.25);
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease;

    &:hover:not(:disabled) {
        border-color: rgba(255, 255, 255, 0.45);
    }

    &:checked {
        background-color: #f45a90;
        border-color: #f45a90;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='10' viewBox='0 0 12 10'%3E%3Cpath d='M1.5 5.2l3 3 6-6.4' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.45;
    }

    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }
`

export const CheckboxInput = styled.input.attrs({ type: 'checkbox' })`
    ${checkboxBase}
    display: block;
`

export const Checkbox = styled.label`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    font-size: 14px;
    line-height: 1.45;
    cursor: pointer;

    input[type='checkbox'] {
        ${checkboxBase}
        margin-top: 0;
    }
`

/* --------------------------------- Banners --------------------------------- */

const bannerTones = {
    info: 'rgba(110, 124, 255, 0.18)',
    warn: 'rgba(255, 179, 64, 0.18)',
    error: 'rgba(255, 60, 79, 0.2)',
    success: 'rgba(32, 189, 111, 0.2)',
}

export const Banner = styled.div<{ $tone?: keyof typeof bannerTones }>`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    padding: 14px 18px;
    border-radius: 15px;
    background: ${({ $tone = 'info' }) => bannerTones[$tone]};
    border: 1px solid rgba(255, 255, 255, 0.08);
    font-size: 15px;

    > p {
        flex: 1;
        min-width: 200px;
    }
`

export const ErrorText = styled.p`
    color: #ff8a94;
    font-size: 14px;
`

/* ---------------------------------- Badge ---------------------------------- */

const badgeTones = {
    neutral: ['rgba(255,255,255,0.1)', 'rgba(255,255,255,0.85)'],
    green: ['rgba(32,189,111,0.2)', '#6ee7a8'],
    blue: ['rgba(110,124,255,0.22)', '#aab4ff'],
    yellow: ['rgba(255,179,64,0.2)', '#ffcc80'],
    red: ['rgba(255,60,79,0.2)', '#ff8a94'],
    pink: ['rgba(244,90,144,0.2)', '#ff9fc3'],
}

export type BadgeTone = keyof typeof badgeTones

export const Badge = styled.span<{ $tone?: BadgeTone }>`
    display: inline-block;
    padding: 2px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    background: ${({ $tone = 'neutral' }) => badgeTones[$tone][0]};
    color: ${({ $tone = 'neutral' }) => badgeTones[$tone][1]};
`

/* ---------------------------------- Table ---------------------------------- */

// Tables scroll inside their card on narrow screens instead of widening the page.
export const TableWrap = styled.div`
    overflow-x: auto;
`

export const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
    font-variant-numeric: tabular-nums;

    th {
        text-align: left;
        color: rgba(255, 255, 255, 0.55);
        font-weight: 600;
        padding: 8px 10px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        white-space: nowrap;
    }

    td {
        padding: 10px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        vertical-align: middle;
    }

    td.num,
    th.num {
        text-align: right;
        white-space: nowrap;
    }

    /* Row actions sit flush right */
    td.actions > * {
        justify-content: flex-end;
    }

    /* Line the outer columns up with the card's content edge */
    th:first-child,
    td:first-child {
        padding-left: 0;
    }

    th:last-child,
    td:last-child {
        padding-right: 0;
    }

    tr:last-child td {
        border-bottom: none;
    }
`

export const EmptyRow = ({ colSpan, children }: { colSpan: number; children: ReactNode }) => (
    <tr>
        <td colSpan={colSpan} style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', padding: 24 }}>
            {children}
        </td>
    </tr>
)

/* ---------------------------------- Tabs ----------------------------------- */

// Tabs have no side padding so the first label lines up with the content above and the underline
// sits exactly under each label
const TabBar = styled.div`
    display: flex;
    gap: 24px;
    overflow-x: auto;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`

const TabButton = styled.button<{ $active: boolean }>`
    ${focusRing}
    background: none;
    padding: 10px 0;
    font-family: inherit;
    font-size: 15px;
    font-weight: 600;
    color: ${({ $active }) => ($active ? 'white' : 'rgba(255,255,255,0.55)')};
    border-bottom: 2px solid ${({ $active }) => ($active ? '#f45a90' : 'transparent')};
    cursor: pointer;
    white-space: nowrap;
`

export function Tabs<T extends string>({
    tabs,
    value,
    onChange,
}: {
    tabs: { value: T; label: ReactNode }[]
    value: T
    onChange: (value: T) => void
}) {
    return (
        <TabBar role="tablist">
            {tabs.map((tab) => (
                <TabButton
                    key={tab.value}
                    type="button"
                    role="tab"
                    aria-selected={tab.value === value}
                    $active={tab.value === value}
                    onClick={() => onChange(tab.value)}
                >
                    {tab.label}
                </TabButton>
            ))}
        </TabBar>
    )
}

/* --------------------------------- Segmented ------------------------------- */

const SegmentBar = styled.div`
    display: inline-flex;
    background: rgba(255, 255, 255, 0.06);
    border-radius: 999px;
    padding: 3px;
`

const Segment = styled.button<{ $active: boolean }>`
    ${focusRing}
    background: ${({ $active }) => ($active ? 'rgba(255,255,255,0.16)' : 'transparent')};
    color: ${({ $active }) => ($active ? 'white' : 'rgba(255,255,255,0.6)')};
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    padding: 6px 12px;
    border-radius: 999px;
    cursor: pointer;
`

export function Segmented<T extends string>({
    options,
    value,
    onChange,
}: {
    options: { value: T; label: string }[]
    value: T
    onChange: (value: T) => void
}) {
    return (
        <SegmentBar>
            {options.map((option) => (
                <Segment
                    key={option.value}
                    type="button"
                    $active={option.value === value}
                    aria-pressed={option.value === value}
                    onClick={() => onChange(option.value)}
                >
                    {option.label}
                </Segment>
            ))}
        </SegmentBar>
    )
}

/* ---------------------------------- Pager ---------------------------------- */

export const Pager = ({
    page,
    totalPages,
    onChange,
}: {
    page: number
    totalPages: number
    onChange: (page: number) => void
}) =>
    totalPages > 1 ? (
        <Row style={{ justifyContent: 'flex-end' }}>
            <SecondaryButton type="button" disabled={page <= 1} onClick={() => onChange(page - 1)}>
                Previous
            </SecondaryButton>
            <Muted>
                Page {page} of {totalPages}
            </Muted>
            <SecondaryButton type="button" disabled={page >= totalPages} onClick={() => onChange(page + 1)}>
                Next
            </SecondaryButton>
        </Row>
    ) : null

/* --------------------------------- Loading --------------------------------- */

const SpinnerRing = styled.div`
    width: 36px;
    height: 36px;
    margin: 40px auto;
    border-radius: 50%;
    border: 4px solid rgba(255, 255, 255, 0.15);
    border-top-color: rgba(255, 255, 255, 0.8);
    animation: affiliate-spin 0.8s linear infinite;

    @keyframes affiliate-spin {
        to {
            transform: rotate(360deg);
        }
    }
`

export const InlineSpinner = () => <SpinnerRing role="status" aria-label="Loading" />
